import { useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const positions = new Map<string, number>();

/** Restore the app's scrolling pane, including pages that load asynchronously. */
export function usePageScroll() {
  const ref = useRef<HTMLElement>(null);
  const location = useLocation();
  const navigationType = useNavigationType();
  useLayoutEffect(() => {
    const pane = ref.current;
    if (!pane) return;
    const target =
      navigationType === 'POP'
        ? (positions.get(location.key) ?? 0)
        : (location.state?.restoreScroll ?? 0);
    let pending = true;
    const restore = () => {
      if (!pending) return;
      pane.scrollTop = target;
      if (pane.scrollTop >= target) pending = false;
    };
    restore();
    const observer = new ResizeObserver(restore);
    if (pane.firstElementChild) observer.observe(pane.firstElementChild);
    const mutationObserver = new MutationObserver(restore);
    mutationObserver.observe(pane, { childList: true, subtree: true });
    const cancel = () => {
      pending = false;
    };
    pane.addEventListener('wheel', cancel, { passive: true });
    pane.addEventListener('touchstart', cancel, { passive: true });
    return () => {
      positions.set(location.key, pane.scrollTop);
      if (positions.size > 50) positions.delete(positions.keys().next().value!);
      observer.disconnect();
      mutationObserver.disconnect();
      pane.removeEventListener('wheel', cancel);
      pane.removeEventListener('touchstart', cancel);
    };
  }, [location.key, location.state, navigationType]);
  return ref;
}
